import lazyLoader from '../utils/http/lazy-loader';
import {useCallback, useEffect, useState, useRef} from 'react';
import {apiClient} from '../http/query-client';
import {RecaptchaAction} from '../core/settings/settings';
import {toast} from '../ui/toast/toast';
import {message} from '../i18n/message';
import {useSettings} from '../core/settings/use-settings';

declare global {
  interface Window {
    turnstile?: any;
    hcaptcha?: any;
  }
}

export function useRecaptcha(action: RecaptchaAction) {
  const {recaptcha} = useSettings();
  const siteKey = recaptcha?.site_key;
  const enable = recaptcha?.enable;
  const captchaType = recaptcha?.type || 'none';
  const enabled = !!(siteKey && captchaType !== 'none' && enable?.[action]);

  const [token, setToken] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    if (enabled && siteKey) {
      if (captchaType === 'recaptcha_invisible') {
        lazyLoader.loadAsset(
          `https://www.google.com/recaptcha/api.js?render=${siteKey}`
        );
      } else if (captchaType === 'recaptcha_v2') {
        lazyLoader.loadAsset(
          `https://www.google.com/recaptcha/api.js?render=explicit`
        );
      } else if (captchaType === 'turnstile') {
        lazyLoader.loadAsset(
          `https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit`
        );
      } else if (captchaType === 'hcaptcha') {
        lazyLoader.loadAsset(
          `https://js.hcaptcha.com/1/api.js?render=explicit`
        );
      }
    }
  }, [enabled, captchaType, siteKey]);

  const verify = useCallback(async () => {
    if (!enabled) return true;
    setIsVerifying(true);

    if (captchaType === 'recaptcha_invisible' && siteKey) {
      const isValid = await executeInvisible(siteKey, action);
      if (!isValid) {
        toast.danger(message('Could not verify you are human.'));
      }
      setIsVerifying(false);
      return isValid;
    }

    if (!token) {
      toast.danger(message('Please complete the captcha challenge.'));
      setIsVerifying(false);
      return false;
    }

    try {
      const response = await apiClient.post('recaptcha/verify', {token});
      const isValid = !!response.data.success;
      if (!isValid) {
        toast.danger(message('Could not verify you are human.'));
      }
      setIsVerifying(false);
      return isValid;
    } catch (e) {
      toast.danger(message('Could not verify you are human.'));
      setIsVerifying(false);
      return false;
    }
  }, [enabled, captchaType, siteKey, action, token]);

  return {
    verify,
    isVerifying,
    captchaType,
    enabled,
    setToken,
    siteKey,
  };
}

async function executeInvisible(
  siteKey: string,
  action: string
): Promise<boolean> {
  return new Promise(resolve => {
    window.grecaptcha?.ready(async () => {
      try {
        const token = await window.grecaptcha?.execute(siteKey, {action});
        const result = await apiClient
          .post('recaptcha/verify', {token})
          .then(r => r.data.success)
          .catch(() => false);
        resolve(result ?? false);
      } catch (err) {
        resolve(false);
      }
    });
  });
}

interface CaptchaWidgetProps {
  captchaType: string;
  siteKey?: string;
  enabled?: boolean;
  onVerify: (token: string | null) => void;
}

export function CaptchaWidget({
  captchaType,
  siteKey,
  enabled,
  onVerify,
}: CaptchaWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<any>(null);

  useEffect(() => {
    if (
      !enabled ||
      !siteKey ||
      !containerRef.current ||
      captchaType === 'none' ||
      captchaType === 'recaptcha_invisible'
    ) {
      return;
    }

    let isMounted = true;
    const container = containerRef.current;

    const initWidget = () => {
      if (!isMounted) return;

      try {
        container.innerHTML = ''; // Clear container before rendering

        if (captchaType === 'turnstile' && window.turnstile) {
          widgetIdRef.current = window.turnstile.render(container, {
            sitekey: siteKey,
            callback: (token: string) => {
              onVerify(token);
            },
            'expired-callback': () => {
              onVerify(null);
            },
            'error-callback': () => {
              onVerify(null);
            },
          });
        } else if (captchaType === 'hcaptcha' && window.hcaptcha) {
          widgetIdRef.current = window.hcaptcha.render(container, {
            sitekey: siteKey,
            callback: (token: string) => {
              onVerify(token);
            },
            'expired-callback': () => {
              onVerify(null);
            },
            'error-callback': () => {
              onVerify(null);
            },
          });
        } else if (captchaType === 'recaptcha_v2' && window.grecaptcha) {
          widgetIdRef.current = (window.grecaptcha as any).render(container, {
            sitekey: siteKey,
            callback: (token: string) => {
              onVerify(token);
            },
            'expired-callback': () => {
              onVerify(null);
            },
            'error-callback': () => {
              onVerify(null);
            },
          });
        }
      } catch (err) {
        console.error('Failed to initialize captcha widget', err);
      }
    };

    const checkInterval = setInterval(() => {
      const isLoaded =
        (captchaType === 'turnstile' && window.turnstile) ||
        (captchaType === 'hcaptcha' && window.hcaptcha) ||
        (captchaType === 'recaptcha_v2' && window.grecaptcha);

      if (isLoaded) {
        clearInterval(checkInterval);
        initWidget();
      }
    }, 300);

    return () => {
      isMounted = false;
      clearInterval(checkInterval);

      try {
        if (
          captchaType === 'turnstile' &&
          window.turnstile &&
          widgetIdRef.current !== null
        ) {
          window.turnstile.remove(widgetIdRef.current);
        } else if (
          captchaType === 'hcaptcha' &&
          window.hcaptcha &&
          widgetIdRef.current !== null
        ) {
          window.hcaptcha.reset(widgetIdRef.current);
        }
      } catch (e) {
        // Ignore cleanup errors
      }
    };
  }, [captchaType, siteKey, enabled, onVerify]);

  if (
    !enabled ||
    captchaType === 'none' ||
    captchaType === 'recaptcha_invisible'
  ) {
    return null;
  }

  return (
    <div className="mb-24 flex justify-center md:justify-start">
      <div ref={containerRef} />
    </div>
  );
}
